import type { PlanOption } from '@/@types/trackheros'

export type PlanInterval = 'monthly' | 'yearly'

/** One product on the price list, with its monthly and yearly rows. */
export type PlanGroup = {
    tier: string
    monthly?: PlanOption
    yearly?: PlanOption
    sort: number
}

/**
 * Pair each plan's monthly and yearly rows by `tier`, for the Monthly / Yearly
 * toggle. A plan with no tier (older rows, bespoke plans) is its own group, so
 * nothing the server offers is dropped from the picker.
 */
export const groupPlans = (plans: PlanOption[]): PlanGroup[] => {
    const groups = new Map<string, PlanGroup>()
    for (const plan of plans) {
        const tier = plan.tier || plan.code
        const group = groups.get(tier) ?? { tier, sort: plan.sort_order }
        group[plan.interval === 'yearly' ? 'yearly' : 'monthly'] = plan
        group.sort = Math.min(group.sort, plan.sort_order)
        groups.set(tier, group)
    }
    return [...groups.values()].sort((a, b) => a.sort - b.sort)
}

/** The row to show for an interval, falling back to the one that exists. */
export const planFor = (group: PlanGroup, interval: PlanInterval) =>
    group[interval] ?? group.monthly ?? group.yearly

export const hasBothIntervals = (groups: PlanGroup[]) =>
    groups.some((group) => group.monthly && group.yearly)

export const isPerSeat = (plan: PlanOption) =>
    plan.kind === 'enterprise' && (plan.seat_price_cents ?? 0) > 0

/** What one seat (or the plan) costs a month, the yearly price spread over twelve. */
export const monthlyCents = (plan: PlanOption) => {
    const cents = isPerSeat(plan)
        ? (plan.seat_price_cents ?? 0)
        : plan.amount_cents
    return plan.interval === 'yearly' ? cents / 12 : cents
}

export const formatMoney = (cents: number, currency: string, locale: string) =>
    new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
        maximumFractionDigits: 2,
    }).format(cents / 100)

/** Minutes to hours for display: the price list sells hours. */
export const toHours = (minutes: number) => {
    const hours = minutes / 60
    return Number.isInteger(hours) ? hours : Math.round(hours * 10) / 10
}

export const costsMoney = (plan: PlanOption) =>
    plan.amount_cents > 0 || isPerSeat(plan)
